import { Button, Flex, Icon, Td, Text, Tr, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import {
  ContractOrgParticipantDto,
  ContractRoleType,
  useDeleteContractParticipantMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";

interface Props {
  contractId: string;
  contractTitle: string;
  onRemoved: () => Promise<void>;
  participant: ContractOrgParticipantDto;
  canDelete: boolean;
}

export const OrganizationParticipantRow = ({ contractId, contractTitle, onRemoved, participant, canDelete }: Props) => {
  const { t } = useTranslation();
  const [remove] = useDeleteContractParticipantMutation();
  const confirmationModal = useConfirmationModal();
  const toast = useToast();
  const displayer = useApiError();

  const removeParticipant = async () => {
    try {
      await remove({
        contractId: contractId,
        deleteContractParticipantRequest: {
          organizationId: participant.id,
        },
      }).unwrap();
      await onRemoved();
      toast({
        title: t("Participant removed"),
        status: "success",
      });
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const confirmRemoval = () =>
    confirmationModal.show({
      title: t("Remove participant?"),
      description: t("Everyone in {{organization}} loses access to the contract {{contract}}.", {
          organization: participant.name,
          contract: contractTitle,
        }),
      confirmButtonText: t("Remove") ?? "",
      confirmButtonColor: "red",
      onConfirmed: removeParticipant,
    });

  const getPermissionNameTranslationKey = (name: ContractRoleType): string => {
    switch (name) {
      case ContractRoleType.Counterparty:
        return t("Counterparty");
      case ContractRoleType.Editor:
        return t("Editor");
      case ContractRoleType.Viewer:
        return t("Viewer");
    }
  };

  return (
    <Tr key={participant.id}>
      <Td>
        <Text fontSize={"sm"}>{participant.name}</Text>
      </Td>
      <Td>
        <Text fontSize={"sm"}>{t(getPermissionNameTranslationKey(participant.roleType))}</Text>
      </Td>
      <Td>
        {canDelete && (
          <Flex justifyContent="end">
            <Button
              leftIcon={<Icon as={FaTimes} w="15px" h="15px" />}
              colorScheme="red"
              variant="outline"
              size={"xs"}
              marginRight={1}
              onClick={confirmRemoval}
            >
              {t("Delete")}
            </Button>
          </Flex>
        )}
        {confirmationModal.modal}
      </Td>
    </Tr>
  );
};
